import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { backLink, exerciseThumb, setSummaryText } from "../components/common.js";
import { spinner, errorState, toast, confirmDialog } from "../components/ui.js";
import { fmtDurationLong, fmtVolume, fmtDateTime, fmtDistance, fmtPace, weightLabel, fmtDuration, parseDate } from "../lib/format.js";
import { lineChart } from "../components/Chart.js";

export default function SessionDetail(params) {
  const root = h("div", { class: "screen" });
  load();

  async function load() {
    setChildren(root, backLink("History", "/history"), spinner());
    try {
      const s = await api.session(params.id);
      render(s);
    } catch (e) {
      setChildren(root, backLink("History", "/history"), errorState(e.message, load));
    }
  }

  function render(s) {
    let totalVolume = 0;
    let totalSets = 0;
    for (const ex of s.exercises) {
      for (const set of ex.sets) {
        if (set.is_completed && !set.is_warmup) {
          totalVolume += (set.weight_kg || 0) * (set.reps || 0);
          totalSets++;
        }
      }
    }

    // Cardio summary: distance is on the (completed) cardio sets; HR/calories on the session.
    const distance = s.exercises.reduce((m, ex) => m + ex.sets.reduce((a, st) => a + (st.is_completed ? (st.distance_m || 0) : 0), 0), 0);
    const hasCardio = distance > 0 || s.avg_hr != null || s.calories != null;

    setChildren(root,
      backLink("History", "/history"),
      h("h1", { style: { margin: "4px 0" } }, s.name || "Workout"),
      h("div", { class: "hstack", style: { gap: "8px", flexWrap: "wrap" } },
        h("span", { class: "dim" }, fmtDateTime(s.started_at)),
        s.source === "health_connect"
          ? h("span", { class: "chip", style: { gap: "4px" }, title: "Imported from Health Connect" }, icon("pulse", { size: 13 }), "Health Connect")
          : null
      ),

      h(
        "div",
        { class: "stat-grid mt" },
        stat(fmtDurationLong(s.duration_seconds || 0), "Duration"),
        stat(String(totalSets), "Sets"),
        stat(fmtVolume(totalVolume).split(" ")[0], "Volume (" + weightLabel() + ")"),
        stat(String(s.exercises.length), "Exercises")
      ),

      hasCardio
        ? h(
            "div",
            { class: "stat-grid mt" },
            distance > 0 ? stat(fmtDistance(distance), "Distance") : null,
            distance > 0 ? stat(fmtPace(s.duration_seconds, distance) || "—", "Pace") : null,
            s.avg_hr != null ? stat(`${s.avg_hr}${s.max_hr != null ? ` / ${s.max_hr}` : ""}`, "Avg / max HR") : null,
            s.calories != null ? stat(String(s.calories), "Calories") : null
          )
        : null,

      hrCard(s),

      s.notes ? h("div", { class: "card mt" }, h("b", null, "Notes"), h("p", { class: "muted", style: { marginTop: "4px" } }, s.notes)) : null,

      h("div", { class: "section-title" }, "Exercises"),
      ...s.exercises.map(exCard),

      h("button", { class: "btn btn-block mt-lg", style: { background: "transparent", color: "var(--danger)" }, onClick: () => del(s) }, icon("trash", { size: 18 }), "Delete this record")
    );
  }

  // ── Heart-rate curve ─────────────────────────────────────────────────────────
  // Only sessions recorded with a band paired have samples at all, so for almost every workout
  // this card is absent rather than empty — an empty chart would read as "your heart rate was
  // flat", which is a different and wrong claim.
  //
  // Set completions are marked along the same axis: which set actually cost the most, and how
  // long the rests really ran, is precisely what the set table below cannot show.
  function hrCard(s) {
    const samples = s.hr_samples || [];
    if (samples.length < 2) return null; // one reading is a number, not a curve
    const startMs = parseDate(s.started_at)?.getTime() ?? null;

    const marks = [];
    if (startMs != null) {
      for (const ex of s.exercises) {
        for (const set of ex.sets) {
          if (!set.is_completed || set.is_warmup) continue;
          const at = parseDate(set.completed_at)?.getTime();
          // Sets logged before the band's first reading fall outside the plot; lineChart drops
          // them rather than stretching the x-range to reach a marker with no curve under it.
          if (Number.isFinite(at)) marks.push({ x: Math.round((at - startMs) / 1000) });
        }
      }
    }

    return h("div", { class: "card mt" },
      h("div", { class: "row-between", style: { marginBottom: "6px" } },
        h("b", null, "Heart rate"),
        h("span", { class: "dim", style: { fontSize: ".78rem" } },
          `${samples.length} reading${samples.length === 1 ? "" : "s"}`)),
      lineChart(samples.map((p) => ({ x: p.t, y: p.bpm })), {
        height: 150,
        xScale: "time",
        markers: marks,
        format: (v) => `${Math.round(v)} bpm`,
        // x is an offset from the session start, in seconds — elapsed time, not a clock time.
        xFormat: (v) => fmtDuration(Math.max(0, Math.round(v))),
      }),
      h("div", { class: "dim", style: { fontSize: ".76rem", marginTop: "8px", lineHeight: 1.45 } },
        marks.length ? "Vertical lines mark completed sets. " : "",
        // Being honest about the resolution matters: a smooth line invites the reader to believe
        // in detail that simply isn't in the data.
        "Your band writes to Health Connect roughly once a minute, so this is the shape of the workout rather than a beat-by-beat trace.")
    );
  }

  function exCard(ex) {
    const done = ex.sets.filter((s) => s.is_completed);
    const mt = ex.metric_type || "weight_reps";
    let n = 0;
    return h(
      "div",
      { class: "card", style: { marginBottom: "10px" } },
      h(
        "div",
        { class: "hstack", style: { marginBottom: "8px" } },
        exerciseThumb(ex.images, { size: 40 }),
        h("div", { class: "ex-name grow", onClick: () => navigate(`/exercise/${ex.library_exercise_id}`) }, ex.exercise_name)
      ),
      ex.notes ? h("div", { class: "muted", style: { fontSize: ".82rem", marginBottom: "8px", whiteSpace: "pre-wrap" } }, ex.notes) : null,
      h(
        "div",
        { class: "set-grid" },
        ...done.map((set) => {
          if (!set.is_warmup) n++;
          return h(
            "div",
            { class: "row-between", style: { padding: "5px 2px" } },
            h("span", { class: ["set-num", set.is_warmup && "warmup"] }, set.is_warmup ? "W" : String(n)),
            h("span", { class: "grow", style: { fontWeight: 600, textAlign: "center" } }, setSummaryText(mt, set)),
            h("span", { class: "dim", style: { width: "54px", textAlign: "right" } }, set.rpe ? `RPE ${set.rpe}` : "")
          );
        }),
        done.length === 0 ? h("p", { class: "dim center" }, "No completed sets") : null
      )
    );
  }

  function stat(num, label) {
    return h("div", { class: "stat" }, h("div", { class: "num accent" }, num), h("div", { class: "lbl" }, label));
  }

  async function del(s) {
    const ok = await confirmDialog({ title: "Delete record?", message: "This workout record will be permanently removed.", confirmText: "Delete", danger: true });
    if (!ok) return;
    try {
      await api.deleteSession(s.id);
      toast("Record deleted", "success");
      navigate("/history");
    } catch (e) {
      toast(e.message, "error");
    }
  }

  return root;
}
